import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { api, ago, errMsg, TASK_LABELS } from "@/lib/api";
import { Loading, PageHeader, StatusPill } from "@/components/common";

function Stat({ label, value, sub, testId, to }) {
  const body = (<><p className="eyebrow">{label}</p><p className="mt-1 font-display text-3xl font-bold" data-testid={testId}>{value}</p>{sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}</>);
  return to ? <Link to={to} className="panel p-5 hover:border-primary/30">{body}</Link> : <div className="panel p-5">{body}</div>;
}

export default function AdminOverview() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/overview").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  if (!d) return <Loading />;
  const b = d.budget;
  const pct = Math.min(100, (b.spent / b.monthly_cap_usd) * 100);
  return (
    <div>
      <PageHeader eyebrow="Super Admin · MFA verified" title="Platform overview" testId="admin-overview-heading" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Users" value={d.users} testId="admin-stat-users" />
        <Stat label="Active jobs" value={d.jobs_active} testId="admin-stat-jobs" to="/admin/sources" />
        <Stat label="Active AI tasks" value={`${d.active_routes}/${d.total_tasks}`} testId="admin-stat-routes" to="/admin/routes" sub="Benchmarked & switched on" />
        <Stat label={`AI requests · ${b.month}`} value={d.requests_month} sub={`${d.failed_month} failed`} testId="admin-stat-requests" to="/admin/usage" />
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <section className="panel p-6" data-testid="admin-budget-card">
          <p className="eyebrow mb-3">Monthly AI budget (estimated)</p>
          <p className="font-display text-2xl font-bold">${b.spent.toFixed(2)} <span className="text-sm text-muted-foreground">of ${b.monthly_cap_usd} hard cap</span></p>
          <div className="mt-3 h-2 rounded-full bg-muted"><div className={`h-2 rounded-full ${pct >= b.alert_pct ? "bg-amber-500" : "bg-brand"}`} style={{ width: `${pct}%` }} /></div>
          <p className="mt-2 text-xs text-muted-foreground">Alert at {b.alert_pct}% · ${b.reserved.toFixed(4)} reserved in-flight. Estimates are not invoices — reconcile on the Usage page.</p>
        </section>
        <section className="panel p-6" data-testid="admin-provider-health">
          <p className="eyebrow mb-3">Provider health</p>
          <ul className="space-y-2">{d.providers.map((p) => (
            <li key={p.provider} className="flex items-center justify-between text-sm"><span className="capitalize">{p.provider}</span>
              <span className="flex items-center gap-2 text-xs text-muted-foreground">{p.last_test ? `tested ${ago(p.last_test.at)}` : "never tested"}
                <StatusPill status={p.last_test ? (p.last_test.ok ? p.status : "failed") : p.status === "not configured" ? "disabled" : p.status} label={p.last_test && !p.last_test.ok ? "test failed" : p.status} /></span></li>))}</ul>
        </section>
      </div>
      <section className="panel mt-6 p-6" data-testid="admin-source-health">
        <p className="eyebrow mb-3">Job source health</p>
        {d.sources.length === 0 ? <p className="text-sm text-muted-foreground">No boards registered. No employer board is enabled by default.</p> : (
          <ul className="divide-y">{d.sources.map((s) => (
            <li key={s.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm"><span>{s.employer_name} <span className="text-muted-foreground">· {s.type}</span></span>
              <span className="flex items-center gap-2 text-xs text-muted-foreground">{s.last_sync ? `${s.last_sync.ok ? "synced" : "failed"} ${ago(s.last_sync.at)}` : "never synced"}<StatusPill status={s.status} /><StatusPill status={s.health} /></span></li>))}</ul>
        )}
      </section>
    </div>
  );
}

export function AdminAudit() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => api.get("/admin/audit", { params: { action: q } }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))), 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div>
      <PageHeader eyebrow="Append-only" title="Audit log" testId="admin-audit-heading">
        <Input placeholder="Filter by action prefix, e.g. provider" value={q} onChange={(e) => setQ(e.target.value)} className="w-64" data-testid="audit-filter-input" />
      </PageHeader>
      {!rows ? <Loading /> : (
        <div className="panel overflow-x-auto">
          <table className="w-full text-sm" data-testid="audit-table">
            <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="p-3">When</th><th className="p-3">Actor</th><th className="p-3">Action</th><th className="p-3">Target</th><th className="p-3">Details</th></tr></thead>
            <tbody>{rows.map((r) => (
              <tr key={r.id} className="border-b last:border-0 align-top"><td className="whitespace-nowrap p-3 text-xs">{new Date(r.at).toLocaleString()}</td><td className="p-3 text-xs">{r.actor_email}</td>
                <td className="p-3 font-mono text-xs">{r.action}</td><td className="p-3 text-xs">{TASK_LABELS[r.target] || r.target}</td>
                <td className="max-w-xs truncate p-3 font-mono text-[11px] text-muted-foreground" title={JSON.stringify(r.details)}>{JSON.stringify(r.details)}</td></tr>))}</tbody>
          </table>
          {rows.length === 0 && <p className="p-6 text-sm text-muted-foreground">No entries.</p>}
        </div>
      )}
    </div>
  );
}
